/**
 * Shared react-markdown link renderer.
 *
 * Problem it solves: react-markdown's default `<a>` renders a plain
 * same-tab link. Inside CHAOS the chat / session panes live in a
 * long-lived SPA tab that holds unsaved work (a running session, a
 * half-written note). A same-tab navigation to an external URL blows
 * that work away. So every markdown surface that shows agent output or
 * user notes must open EXTERNAL links in a new tab.
 *
 * Single source of truth: `ui/markdown.tsx` (the everyday <Markdown>
 * renderer) and `agent/markdown-content.tsx` (the heavy chat renderer)
 * and `editor/MarkdownEditor.tsx` (the authoring preview pane) all wire
 * this same `markdownComponents` map into react-markdown's `components`
 * prop, so link behaviour can never drift between surfaces.
 *
 * internal vs external policy:
 *   - Absolute http(s) URLs and protocol-relative `//host/…` URLs are
 *     treated as EXTERNAL → `target="_blank" rel="noopener noreferrer"`.
 *     `rel` is mandatory: without `noopener` the opened page can reach
 *     back through `window.opener`; `noreferrer` also strips the
 *     Referer header.
 *   - In-page anchors (`#section`), root/relative paths (`/board/…`,
 *     `./file.md`) and non-web schemes (`mailto:`, `tel:`) are left in
 *     the SAME tab. Relative paths may be SPA router targets — forcing
 *     a new tab there would break in-app navigation and re-download the
 *     whole bundle; `mailto:`/`tel:` hand off to the OS, not a tab.
 */
import * as React from 'react';

/**
 * True when `href` points at a different origin the browser would load
 * as a full page (http/https absolute, or protocol-relative //host).
 * Anything relative, hash-only, or a non-web scheme returns false.
 */
export function isExternalHref(href: string | undefined | null): boolean {
  if (!href) return false;
  const h = href.trim();
  if (!h) return false;
  // Protocol-relative (//example.com/…) → resolves to http(s), external.
  if (h.startsWith('//')) return true;
  // Absolute http/https only. Other schemes (mailto:, tel:, data:, etc.)
  // are intentionally NOT forced into a new tab.
  return /^https?:\/\//i.test(h);
}

type AnchorProps = {
  children?: React.ReactNode;
  href?: string;
};

/**
 * `<a>` override for react-markdown. External links open in a new tab
 * with a hardened `rel`; internal / hash / scheme links keep default
 * (same-tab) behaviour so SPA navigation and intra-doc anchors survive.
 */
export function MarkdownLink({ children, href, ...rest }: AnchorProps) {
  const external = isExternalHref(href);
  return (
    <a
      {...rest}
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="font-medium underline-offset-2 hover:underline"
    >
      {children}
    </a>
  );
}

/**
 * `components` map fragment to spread into react-markdown. Kept as a
 * standalone object so callers that already override other elements
 * (p / li / strong for mention pills) can merge it in.
 */
export const markdownComponents = {
  a: MarkdownLink,
};
